import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  UserGroupIcon, 
  ClockIcon,
  ArrowRightIcon,
  TvIcon,
  CalendarIcon,
  SpeakerWaveIcon,
  DocumentTextIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { apiService, Channel, Show, Episode } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Breadcrumb from '../components/Breadcrumb';
import { useAudio } from '../contexts/AudioContext';

const EpisodeDetail: React.FC = () => {
  const { channelId, showId, episodeId } = useParams<{ channelId: string; showId: string; episodeId: string }>();
  const navigate = useNavigate();
  const { startSession } = useAudio();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [show, setShow] = useState<Show | null>(null);
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [loading, setLoading] = useState(true);
  const [startingSession, setStartingSession] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!channelId || !showId || !episodeId) return;
      
      try {
        const [channelData, showData, episodeData] = await Promise.all([
          apiService.getChannel(channelId),
          apiService.getShow(showId),
          apiService.getEpisode(episodeId)
        ]);
        
        setChannel(channelData);
        setShow(showData);
        setEpisode(episodeData);
      } catch (error) {
        console.error('Error fetching episode data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [channelId, showId, episodeId]);

  const handleStartSession = async (episodeId: string, episodeTitle: string) => {
    try {
      setStartingSession(episodeId);
      await startSession(episodeId, episodeTitle);
      // Audio will start playing automatically via the floating player
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Failed to start session. Please try again.');
    } finally {
      setStartingSession(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!channel || !show || !episode) {
    return (
      <div className="text-center py-12">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Episode Not Found
        </h3>
        <button
          onClick={() => navigate('/channels')}
          className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300"
        >
          <ArrowRightIcon className="w-5 h-5" />
          <span>Back to Channels</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Breadcrumb Navigation */}
      <Breadcrumb 
        items={[
          { label: 'Channels', href: '/channels' },
          { label: channel.name, href: `/channels/${channelId}` },
          { label: show.name, href: `/channels/${channelId}/shows/${showId}` },
          { label: episode.title, isCurrent: true }
        ]} 
      />

      {/* Episode Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {episode.title}
            </h1>
            {episode.description && (
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                {episode.description}
              </p>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-400">
                <CalendarIcon className="w-5 h-5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Published</p>
                  <p>{new Date(episode.publishedAt || episode.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</p>
                </div>
              </div>
              
              {episode.duration && (
                <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-400">
                  <ClockIcon className="w-5 h-5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Duration</p>
                    <p>{Math.round(episode.duration / 60)} minutes</p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-400">
                <SpeakerWaveIcon className="w-5 h-5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Show</p>
                  <p className="hover:text-purple-600 dark:hover:text-purple-400 cursor-pointer" 
                     onClick={() => navigate(`/channels/${channelId}/shows/${showId}`)}>
                    {show.name}
                  </p>
                </div>
              </div>
            </div>

            {/* Tags */}
            {episode.tags && episode.tags.length > 0 && (
              <div className="flex items-center space-x-2">
                <TagIcon className="w-5 h-5 text-gray-500" />
                <div className="flex flex-wrap gap-2">
                  {episode.tags.map((tag, index) => (
                    <span key={index} className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="ml-8">
            <button
              onClick={() => handleStartSession(episode._id, episode.title)}
              disabled={startingSession === episode._id}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-3 shadow-lg hover:shadow-xl"
            >
              {startingSession === episode._id ? (
                <>
                  <div className="w-6 h-6 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span>Starting Session...</span>
                </>
              ) : (
                <>
                  <PlayIcon className="w-6 h-6" />
                  <span>Play Episode</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Episode Segments */}
      {episode.segmentCount && episode.segmentCount > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
          <div className="flex items-center space-x-2 mb-4">
            <DocumentTextIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Episode Segments</h2>
          </div>
          <div className="text-gray-700 dark:text-gray-300">
            <p>This episode contains <strong>{episode.segmentCount}</strong> segments with a total duration of <strong>{episode.duration ? Math.round(episode.duration / 60) : 'unknown'} minutes</strong>.</p>
            {episode.wordCount && (
              <p className="mt-2">Total word count: <strong>{episode.wordCount.toLocaleString()}</strong> words</p>
            )}
          </div>
        </div>
      )}

      {/* Show Information */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Show Information</h2>
          <button
            onClick={() => navigate(`/channels/${channelId}/shows/${showId}`)}
            className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium flex items-center space-x-1"
          >
            <span>View All Episodes</span>
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{show.name}</h3>
            {show.description && (
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                {show.description}
              </p>
            )}
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <UserGroupIcon className="w-4 h-4" />
              <span>{show.panelistIds.length} panelist{show.panelistIds.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          
          <div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                <ClockIcon className="w-4 h-4" />
                <span>Frequency: {show.frequency}</span>
              </div>
              {show.category && (
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                    {show.category}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Channel Information */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Channel Information</h2>
          <button
            onClick={() => navigate(`/channels/${channelId}`)}
            className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium flex items-center space-x-1"
          >
            <span>View Channel</span>
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>
        
        <div className="flex items-center space-x-4">
          {channel.logoUrl ? (
            <img 
              src={channel.logoUrl} 
              alt={channel.name}
              className="w-16 h-16 rounded-lg object-cover"
            />
          ) : (
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <TvIcon className="w-8 h-8 text-white" />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{channel.name}</h3>
            {channel.description && (
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {channel.description}
              </p>
            )}
            {channel.category && (
              <span className="inline-block px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full text-xs mt-2">
                {channel.category}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EpisodeDetail;
